import React, { useState, useEffect } from 'react';
import { getPersonasConfig, getProgramsCatalog } from '../utils/database';
import '../styles/Onboarding.css';

/**
 * Reusable persona + program selection component.
 * Extracted from OnboardingFlow Step 2.
 *
 * Props:
 *   initialPersonaId  - optional pre-selected persona
 *   initialProgramId  - optional pre-selected program
 *   onSelect(personaId, programId, programData) - called when user picks a program
 *   submitLabel       - label for the confirm button (default "Confirm")
 *   loading           - disable the button while parent is saving
 */
function ProgramSelector({ initialPersonaId, initialProgramId, onSelect, submitLabel = 'Confirm', loading = false }) {
  const [personasData, setPersonasData] = useState(null);
  const [programsCatalog, setProgramsCatalog] = useState({});
  const [loadError, setLoadError] = useState(false);

  const [selectedPersonaId, setSelectedPersonaId] = useState(initialPersonaId || '');
  const [selectedProgramId, setSelectedProgramId] = useState(initialProgramId || '');
  const [showingPrograms, setShowingPrograms] = useState(!!(initialPersonaId));

  useEffect(() => {
    getPersonasConfig().then((data) => {
      if (data?.personas) {
        setPersonasData(data);
      } else {
        setLoadError(true);
      }
    }).catch(() => setLoadError(true));
    getProgramsCatalog().then(setProgramsCatalog).catch(() => {});
  }, []);

  const personas = personasData?.personas || [];
  const selectedPersona = personas.find((p) => p.id === selectedPersonaId);
  const availablePrograms = (selectedPersona?.programs || []).map((prog) => {
    const live = prog.programId ? programsCatalog[prog.programId] : null;
    return live ? { ...prog, name: live.name, description: live.description || prog.description } : prog;
  });
  const selectedProgram = availablePrograms.find((p) => p.id === selectedProgramId);

  useEffect(() => {
    if (selectedProgramId && selectedPersona && !selectedPersona.programs.find((p) => p.id === selectedProgramId)) {
      setSelectedProgramId('');
    }
  }, [selectedPersonaId, selectedProgramId, selectedPersona]);

  if (!personasData) {
    return (
      <div className="onboarding-persona-layout">
        <div className="onboarding-card onboarding-persona-select" style={{ textAlign: 'center' }}>
          {loadError ? (
            <p style={{ color: '#FF0000' }}>Failed to load programs. Please try again.</p>
          ) : (
            <p style={{ color: 'rgba(255,255,255,0.5)' }}>Loading programs...</p>
          )}
        </div>
      </div>
    );
  }

  const isValid = !!selectedPersonaId && !!selectedProgramId;

  const handleConfirm = () => {
    if (!isValid || loading) return;
    onSelect(selectedPersonaId, selectedProgramId, selectedProgram);
  };

  return (
    <div className="onboarding-persona-layout">
      {/* Left: Path + Program Selection with slide transition */}
      <div className="onboarding-card onboarding-persona-select">
        <h2 className="onboarding-card-title">Choose Your Program</h2>

        <div className="onboarding-slide-container">
          <div className={`onboarding-slide-wrapper ${showingPrograms ? 'showing-programs' : ''}`}>
            {/* Panel 1: Persona cards */}
            <div className="onboarding-slide-panel">
              <div className="onboarding-path-cards">
                {personas.map((persona) => (
                  <button
                    key={persona.id}
                    type="button"
                    className={`c-ripple onboarding-path-card ${selectedPersonaId === persona.id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedPersonaId(persona.id);
                      setSelectedProgramId('');
                      setShowingPrograms(true);
                    }}
                  >
                    <div className="onboarding-path-card-name">{persona.name}</div>
                    <div className="onboarding-path-card-desc">{persona.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Panel 2: Programs for selected persona */}
            <div className="onboarding-slide-panel">
              {selectedPersona && (
                <>
                  <button
                    type="button"
                    className="c-ripple onboarding-breadcrumb"
                    onClick={() => {
                      setShowingPrograms(false);
                      setSelectedProgramId('');
                    }}
                  >
                    &larr; Paths
                  </button>
                  <h3 className="onboarding-programs-heading">{selectedPersona.name} Programs</h3>
                  <div className="onboarding-persona-options">
                    {availablePrograms.map((program) => (
                      <button
                        key={program.id}
                        className={`c-ripple onboarding-persona-option ${selectedProgramId === program.id ? 'selected' : ''} ${program.status === 'coming_soon' ? 'coming-soon' : ''}`}
                        onClick={() => {
                          if (program.status === 'coming_soon') return;
                          setSelectedProgramId(program.id);
                        }}
                        disabled={program.status === 'coming_soon'}
                      >
                        <span className="onboarding-program-name">{program.name}</span>
                        {program.status === 'coming_soon' && (
                          <span className="onboarding-program-badge">Coming Soon</span>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <button
          onClick={handleConfirm}
          className="c-ripple onboarding-submit"
          disabled={!isValid || loading}
        >
          {loading ? 'Please wait...' : submitLabel}
        </button>
      </div>

      {/* Right: Description */}
      <div className="onboarding-card onboarding-persona-detail">
        {selectedProgram ? (
          <>
            <h2 className="onboarding-card-title">{selectedProgram.name}</h2>
            <div className="onboarding-persona-content">
              <p className="onboarding-persona-description">{selectedProgram.description}</p>
              {selectedPersona.mindset && (
                <p className="onboarding-persona-mindset">"{selectedPersona.mindset}"</p>
              )}
              {selectedPersona.whoFits && (
                <>
                  <h3>Who This Fits:</h3>
                  <ul className="onboarding-persona-list">
                    {selectedPersona.whoFits.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
              {selectedPersona.periodization && (
                <>
                  <h3>Periodization Structure:</h3>
                  <p><strong>Macrocycle:</strong> {selectedPersona.periodization.macrocycle}</p>
                  {selectedPersona.periodization.mesocycles && (
                    <>
                      <p><strong>Mesocycles (12 weeks each):</strong></p>
                      <ul className="onboarding-persona-list onboarding-mesocycle-list">
                        {selectedPersona.periodization.mesocycles.map((block, i) => (
                          <li key={i}>{block}</li>
                        ))}
                      </ul>
                    </>
                  )}
                  {selectedPersona.periodization.microcycles && (
                    <p><strong>Microcycles:</strong> {selectedPersona.periodization.microcycles}</p>
                  )}
                </>
              )}
              {selectedPersona.keyDifferentiator && (
                <>
                  <h3>Key Differentiator:</h3>
                  <p>{selectedPersona.keyDifferentiator}</p>
                </>
              )}
              {selectedPersona.sampleExercises && (
                <>
                  <h3>Sample Exercises:</h3>
                  <p>{selectedPersona.sampleExercises}</p>
                </>
              )}
            </div>
          </>
        ) : showingPrograms && selectedPersona ? (
          <div className="onboarding-persona-placeholder">
            <p>Select a program to see details</p>
          </div>
        ) : (
          <div className="onboarding-persona-placeholder">
            <p>Select a path to get started</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProgramSelector;
